import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import React, { useCallback, useEffect, useState } from "react";
import { Image, Pressable, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import AddSlotIcon from "@/assets/svgs/add-slot-icon";
import ClockIcon from "@/assets/svgs/clock-icon";
import DownArrowIcon from "@/assets/svgs/down-arrow-icon";
import SwitchOffIcon from "@/assets/svgs/switch-off-icon";
import SwitchOnIcon from "@/assets/svgs/switch-on-icon";
import { Button } from "@/elements/components/ui/button";
import { CheckboxInput } from "@/elements/components/ui/checkbox-input";
import { Tag } from "@/elements/components/ui/tag";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import Loader from "../shared/loader";
import { useSheet } from "../shared/sheet-provider";
import type {
  AssignTimeSlotSheetPayload,
  AssignTimeSlotSheetResult,
} from "../sheets/assign-time-slot-sheet";
import type {
  TimezoneSearchSheetPayload,
  TimezoneSearchSheetResult,
} from "../sheets/timezone-search-sheet";
import { api } from "../utils/convex-api";
import { SheetIds } from "../utils/sheet-ids";

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

const dayOfWeekToLabel = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function showSavedToast() {
  Toast.show({ type: "success", text1: "Saved" });
}

function showSaveFailedToast() {
  Toast.show({ type: "error", text1: "Failed to save" });
}

function TutorAvailabilityForm({
  initialData,
}: {
  initialData: typeof api.tutor.profileSettings.queries.getProfile._returnType;
}) {
  const responsiveWidth = useResponsiveWidth();
  const { teachingPreference = [], timezoneId } = initialData;
  const [detectedTimezone, setDetectedTimezone] = useState<string | null>(null);
  const [suggestedTimezone, setSuggestedTimezone] = useState<{
    label: string;
    value: Id<"timezones">;
  } | null>(null);
  const { data: allTimezones = [] } = useQuery(
    convexQuery(api.account.timezoneQueries.getAllTimezones, {}),
  );
  const {
    data: allAvailabilityDays = [],
    isLoading: isLoadingAvailabilityDays,
  } = useQuery(
    convexQuery(api.tutor.profileSettings.queries.getAllAvailabilityDays, {}),
  );
  const timezone = allTimezones.find(
    (timezone) => timezone.value === timezoneId,
  );
  const { open: openTimezoneSearch } = useSheet<
    TimezoneSearchSheetPayload,
    TimezoneSearchSheetResult
  >(SheetIds.TIMEZONE_SEARCH);
  const { open: openAssignTimeSlot } = useSheet<
    AssignTimeSlotSheetPayload,
    AssignTimeSlotSheetResult
  >(SheetIds.ASSIGN_TIME_SLOT);
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.updateProfile,
    ),
  });
  const { mutateAsync: createAvailabilityDay } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.createAvailabilityDay,
    ),
  });
  const { mutateAsync: updateAvailabilityDay } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.updateAvailabilityDay,
    ),
  });
  const { mutateAsync: createNewTimeSlot } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.createNewTimeSlot,
    ),
  });
  const { mutateAsync: updateTimeSlot } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.updateTimeSlot,
    ),
  });
  const { mutateAsync: deleteTimeSlot } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.profileSettings.mutations.removeTimeSlot,
    ),
  });

  useEffect(() => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setDetectedTimezone(userTimezone);

      if (allTimezones.length > 0) {
        const matchingTimezone = allTimezones.find(
          (tz) =>
            tz.label.includes(userTimezone) ||
            userTimezone.includes(tz.label.split(" ")[0]),
        );

        if (matchingTimezone && matchingTimezone.value !== timezoneId) {
          setSuggestedTimezone(matchingTimezone);
        }
      }
    } catch (error) {
      console.log("Timezone detection failed:", error);
    }
  }, [allTimezones, timezoneId]);

  const handleSetSuggestedTimezone = useCallback(async () => {
    if (!suggestedTimezone) return;

    try {
      await updateProfile({
        timezoneId: suggestedTimezone.value,
      });
      setSuggestedTimezone(null);
      showSavedToast();
    } catch {
      showSaveFailedToast();
    }
  }, [suggestedTimezone, updateProfile]);

  const handleTimezoneOpen = useCallback(async () => {
    const result = await openTimezoneSearch({
      preSelectedOption: timezoneId ?? undefined,
    });
    if (!result?.selectedOption) return;

    try {
      await updateProfile({
        timezoneId: result.selectedOption,
      });
      showSavedToast();
    } catch {
      showSaveFailedToast();
    }
  }, [openTimezoneSearch, timezoneId, updateProfile]);

  const handleTeachingPreferenceOpen = useCallback(
    async (preference: "in_person" | "remote") => {
      const updatedTeachingPreference = teachingPreference.includes(preference)
        ? teachingPreference.filter((p) => p !== preference)
        : [...teachingPreference, preference];

      try {
        await updateProfile({
          teachingPreference: updatedTeachingPreference,
        });
        showSavedToast();
      } catch {
        showSaveFailedToast();
      }
    },
    [teachingPreference, updateProfile],
  );

  const handleDayToggle = useCallback(
    async (
      day: (typeof api.tutor.profileSettings.queries.getAllAvailabilityDays._returnType)[number],
    ) => {
      try {
        await updateAvailabilityDay({
          id: day._id,
          isDisabled: !day.isDisabled,
        });
        showSavedToast();
      } catch {
        showSaveFailedToast();
      }
    },
    [updateAvailabilityDay],
  );

  const handleAssignTimeSlot = useCallback(
    async (
      day: (typeof api.tutor.profileSettings.queries.getAllAvailabilityDays._returnType)[number],
      preSelectedTimeSlot?: {
        start: string;
        end: string;
        slotId: Id<"tutorTimeSlots">;
      },
    ) => {
      const result = await openAssignTimeSlot({
        preSelectedTimeSlot: preSelectedTimeSlot,
        day: day,
      });
      if (
        !result?.selectedTimeSlot &&
        !result?.slotId
      ) {
        return;
      }

      try {
        if (result.selectedTimeSlot && !result.slotId) {
          await createNewTimeSlot({
            availabilityDayId: day._id,
            startTime: result.selectedTimeSlot.start,
            endTime: result.selectedTimeSlot.end,
          });
        } else if (result.selectedTimeSlot && result.slotId) {
          await updateTimeSlot({
            id: result.slotId,
            startTime: result.selectedTimeSlot.start,
            endTime: result.selectedTimeSlot.end,
          });
        } else if (result.slotId) {
          await deleteTimeSlot({
            id: result.slotId,
          });
        }
        showSavedToast();
      } catch {
        showSaveFailedToast();
      }
    },
    [createNewTimeSlot, openAssignTimeSlot, updateTimeSlot, deleteTimeSlot],
  );

  const handleSetAvailability = useCallback(async () => {
    try {
      await Promise.all(
        daysOfWeek
          .filter(
            (dayOfWeek) =>
              !allAvailabilityDays.some((day) => day.dayOfWeek === dayOfWeek),
          )
          .map((dayOfWeek) =>
            createAvailabilityDay({
              dayOfWeek,
              isDisabled: true,
            }),
          ),
      );
      showSavedToast();
    } catch {
      showSaveFailedToast();
    }
  }, [allAvailabilityDays, createAvailabilityDay]);

  return (
    <View className="gap-6">
      <View className="gap-2">
        <View className="flex-row items-center gap-2">
          <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
            Teaching preference
          </Text>
        </View>
        <View className="gap-4 flex-row">
          <CheckboxInput
            onPress={() => handleTeachingPreferenceOpen("in_person")}
          >
            <CheckboxInput.Label size="sm">In-person</CheckboxInput.Label>
            <CheckboxInput.Icon
              checked={teachingPreference.includes("in_person")}
            />
          </CheckboxInput>
          <CheckboxInput onPress={() => handleTeachingPreferenceOpen("remote")}>
            <CheckboxInput.Label size="sm">Remote</CheckboxInput.Label>
            <CheckboxInput.Icon
              checked={teachingPreference.includes("remote")}
            />
          </CheckboxInput>
        </View>
      </View>
      <View className="gap-2">
        <Pressable
          className="flex-row items-center gap-2"
          onPress={handleTimezoneOpen}
        >
          <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
            Select Time-zone
          </Text>
          <DownArrowIcon pathClassName="stroke-dark" />
        </Pressable>

        <Text className="text-sm text-gray-600 leading-[1.4] font-geologica-regular">
          We only use your timezone for showing availability to parents when
          booking sessions. Everything else uses your local time. Changing your
          timezone won't affect previous session times, only future
          availability.
        </Text>

        {suggestedTimezone && (
          <View className="gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <Text className="text-sm text-blue-700 font-geologica-medium">
              Detected timezone: {detectedTimezone}
            </Text>
            <Text className="text-sm text-blue-600 font-geologica-regular">
              Set your timezone to {suggestedTimezone.label}?
            </Text>
            <View className="flex-row gap-2">
              <Button
                size="sm"
                intent="primary"
                onPress={handleSetSuggestedTimezone}
              >
                <Button.Text size="sm">Yes, use this timezone</Button.Text>
              </Button>
              <Button
                size="sm"
                intent="secondary"
                onPress={() => setSuggestedTimezone(null)}
              >
                <Button.Text size="sm">No thanks</Button.Text>
              </Button>
            </View>
          </View>
        )}

        {!!timezone && (
          <View className="gap-4 flex-row">
            <Tag size="md" className="self-start flex-1">
              <Tag.Text size="md" numberOfLines={0}>
                {timezone.label}
              </Tag.Text>
            </Tag>
          </View>
        )}
      </View>
      <View className="gap-2">
        <View className="flex-row items-center gap-2">
          <Text className="text-base text-black leading-[1.5] font-medium font-geologica-medium">
            Availability
          </Text>
        </View>
        <Text className="text-sm text-black leading-[1.5] font-normal font-geologica-regular">
          Select the days you are free and press the + to share time slots
        </Text>
        <View className="gap-4 flex-row" />
      </View>

      {allAvailabilityDays.length === 7 ? (
        <View className="gap-4">
          {allAvailabilityDays.map((day) => (
            <View className="gap-2" key={day._id}>
              <Text className="text-base text-black leading-[1.5] font-normal font-geologica-regular">
                {dayOfWeekToLabel[day.dayOfWeek]}
              </Text>
              <View className="flex-row gap-2 items-start">
                <View className="flex-row gap-2 w-auto h-auto items-center">
                  <Pressable onPress={() => handleDayToggle(day)}>
                    {!day.isDisabled ? <SwitchOnIcon /> : <SwitchOffIcon />}
                  </Pressable>
                  {!day.isDisabled && (
                    <Pressable onPress={() => handleAssignTimeSlot(day)}>
                      <AddSlotIcon
                        className="size-8"
                        rectStrokeClassName="stroke-teachLoop-textPrimary"
                        pathClassName="stroke-teachLoop-textPrimary"
                      />
                    </Pressable>
                  )}
                </View>
                {!day.isDisabled && (
                  <View className="w-full flex-row flex-wrap gap-2 flex-1">
                    {day.timeSlots.map((timeSlot) => (
                      <Pressable
                        key={timeSlot._id}
                        onPress={() =>
                          handleAssignTimeSlot(day, {
                            start: timeSlot.startTime,
                            end: timeSlot.endTime,
                            slotId: timeSlot._id,
                          })
                        }
                      >
                        <Tag size="md">
                          <Tag.Text size="md">
                            {timeSlot.startTime} - {timeSlot.endTime}
                          </Tag.Text>
                        </Tag>
                      </Pressable>
                    ))}
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
      ) : isLoadingAvailabilityDays ? (
        <Loader />
      ) : (
        <View className="gap-4">
          <View className="flex-1 justify-center items-center bg-teachLoop-creamLight rounded-2xl border p-4 border-gray-200">
            <Image
              source={require("@/assets/images/no-time-slots.png")}
              resizeMode="contain"
              style={{
                width: responsiveWidth - 100,
                height: (responsiveWidth - 100) * (234 / 313),
              }}
            />
            <View className="absolute top-0 left-0 right-0 bottom-0 justify-center items-center">
              <Button
                size="md"
                intent="primary"
                onPress={handleSetAvailability}
                icon
              >
                <Button.Icon
                  as={ClockIcon}
                  pathClassName="stroke-white"
                  className="size-6"
                />
                <Button.Text size="md">Set Availability</Button.Text>
              </Button>
            </View>
          </View>
        </View>
      )}
      <View className="h-4" />
    </View>
  );
}

export default TutorAvailabilityForm;
